import React, { useState } from 'react'

import Card from '@mui/material/Card'
import { SvgIcon, ToggleButton, ToggleButtonGroup } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Slider from '@mui/material/Slider'
import Divider from '@mui/material/Divider'
import SuccessFilled from '@tdcerhverv/parrotfish/dist/icons/Icons/Success_filled.svg'
import { useTheme } from '@mui/material/styles'
import PopOverInfo from '../Form/PopOverInfo'
import { graphql, useStaticQuery } from 'gatsby'
import CallToAction from '../CallToAction'

const numberFormat = new Intl.NumberFormat('da-DK')
const defaultCountValue = 50
const stepCountValue = 50
const minCountValue = 50
const maxCountValue = 3000

const getPrice = (users: number) => {
  switch (true) {
    case users <= 50:
      return 3000
    case users <= 250:
      return 5000
    case users <= 1000:
      return 7500
    case users <= 2999:
      return 10000
    default:
      return 12500
  }
}

const features = [
  'Kom i gang med introduktion',
  'Opfølgende møder',
  'Microsoft 365',
  'Microsoft 365 Defender',
  'Microsoft betinget adgang',
  'Microsoft Defender til skyen',
  'Microsoft Defender til slutpunkt',
  'Microsoft Entra',
  'Microsoft Exchange Online',
  'Microsoft Intune',
  'Microsoft Power BI',
  'Microsoft Power Platform',
  'Microsofts privilegerede identitetsstyring',
  'Microsoft Sentinel',
  'Microsoft SharePoint',
  'Microsoft Teams',
  'DNS',
]

const PricingCalculator = () => {
  const { palette } = useTheme()
  const styles = {
    root: {
      maxWidth: '500px',
      margin: '0 auto',
      padding: '4px',
    },
    card: {
      marginTop: '4px',
      padding: 4,
      borderRadius: '6px',
      backgroundColor: palette.grey[100],
      border: `1px solid ${palette.grey[300]}`,
      boxShadow: 'none',
    },
    features: {
      maxHeight: '300px',
      overflow: 'auto',
      padding: '2px',
      marginTop: '15px',
      marginBottom: '5px',
      '&::-webkit-scrollbar': {
        width: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: palette.primary.main,
        borderRadius: 3,
        width: '1px',
      },
    },
    toggleGroup: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '16px',
      marginBottom: '20px',
    },
    toggleButtonGroup: {
      borderRadius: '50px',
      border: `1px solid ${palette.grey[400]}`,
      overflow: 'hidden',
      padding: '2px',
      height: '40px',
    },
    toggleButton: {
      borderRadius: '50px',
      fontSize: '12px',
      padding: '9px 15px',
      border: 'none',
      '&.Mui-selected': {
        backgroundColor: palette.primary.main,
        color: 'white',
        borderRadius: '50px',
      },
    },
    button: {
      margin: '20px 15px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
    forButton: {
      variant: 'contained',
      backgroundColor: palette.primary.main,
      color: palette.common.white,
      marginTop: '3px',
      padding: '2px',
      width: '100%',
      height: '35px',
    },
    priceTitle: {
      fontWeight: 'bold',
      color: palette.common.black,
    },
    user: {
      margin: 0,
      '& .MuiSvgIcon-root': {
        width: '12px',
        height: '12px',
      },
    },
    slider: {
      marginTop: 2,
      '& .MuiSlider-rail': {
        opacity: '0.5',
        backgroundColor: `${palette.grey[400]}`,
      },
    },
  }
  const [sliderPriceValue, setSliderPriceValue] = useState(defaultCountValue)
  const [showAnnually, setShowAnnually] = useState(false)
  const data = useStaticQuery(graphql`
    {
      calltoAction: contentfulComponentCta(contentful_id: { eq: "5alb2aefEaO5c4RM5asGUk" }) {
        ...callToAction
      }
    }
  `)

  return (
    <Box sx={styles.root}>
      <Card sx={styles.card}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight="bold">
            {sliderPriceValue}
            {sliderPriceValue === maxCountValue ? '+' : ''}
            <Typography variant="body2" textAlign="right" component="span">
              brugere
            </Typography>
            <Typography variant="body2" component="span" sx={styles.user}>
              <PopOverInfo
                title="Op til det samlede antal primære licenserede brugere i din lejer, f.eks. en Microsoft Enterprise- eller Business-licens. Frontline-licenser er udelukket fra denne optælling."
                text="Op til det samlede antal primære licenserede brugere i din lejer, f.eks. en Microsoft Enterprise- eller Business-licens. Frontline-licenser er udelukket fra denne optælling."
              />
            </Typography>
          </Typography>
          <Typography variant="h4" sx={styles.priceTitle}>
            {numberFormat.format(showAnnually ? getPrice(sliderPriceValue) * 11 : getPrice(sliderPriceValue))} DKK
            <Typography variant="body2" textAlign="right" component="span">
              /{showAnnually ? 'år' : 'måned'}
            </Typography>
            <Typography variant="body2" component="span" sx={styles.user}>
              <PopOverInfo title="Prisen er pr. Microsoft 365 lejer." text="Prisen er pr. Microsoft 365 lejer." />
            </Typography>
          </Typography>
        </Box>
        <Slider
          value={sliderPriceValue}
          onChange={(e, newValue) => setSliderPriceValue(newValue as number)}
          step={stepCountValue}
          min={minCountValue}
          max={maxCountValue}
          aria-labelledby="pricing-slider"
          sx={styles.slider}
        />
        <Box sx={styles.toggleGroup}>
          <Divider sx={{ flex: 1 }} />
          <ToggleButtonGroup
            value={showAnnually}
            exclusive
            onChange={() => setShowAnnually(!showAnnually)}
            sx={styles.toggleButtonGroup}
          >
            <ToggleButton value={false} sx={styles.toggleButton}>
              Månedlig
            </ToggleButton>
            <ToggleButton value={true} sx={styles.toggleButton}>
              Årligt
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <Box sx={styles.features}>
          {features.map((feature, index) => (
            <Box key={index} display="flex" alignItems="center" mt={1}>
              <SvgIcon
                viewBox="0 0 48 48"
                component={SuccessFilled}
                sx={{ color: palette.primary.main, width: '16px', height: '16px', mr: '10px' }}
              />
              <Typography variant="body2">{feature}</Typography>
            </Box>
          ))}
        </Box>
        <Divider sx={{ flex: 1 }} />
        <Box sx={styles.button}>{data?.calltoAction && <CallToAction {...data.calltoAction} />}</Box>
      </Card>
    </Box>
  )
}
export default PricingCalculator
